import logger from './utils/logger';
import { StrictMode, Component, type ErrorInfo, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import { MantineProvider } from '@mantine/core'
import { Notifications } from '@mantine/notifications'
import '@mantine/core/styles.css'
import '@mantine/notifications/styles.css'
import './index.css'
import App from './App.tsx'
import { theme } from './theme'
import { initCrossDeviceSupport } from './utils/crossDeviceInit'
import { setupViewportHeightMonitoring } from './utils/viewportHeight'
import './utils/polyfills'

// Set initial controlbar height CSS variable early to prevent layout shift
// Use orientation-specific defaults based on typical Safari controlbar heights
// Portrait: ~54px, Landscape: ~75px
// Actual measurement will update this value once the controlbar renders
const isPortrait = window.innerHeight > window.innerWidth;
const defaultHeight = isPortrait ? '60px' : '80px'; // Slightly larger than typical to be safe
document.documentElement.style.setProperty('--mobile-controls-height', defaultHeight);

// Setup unified viewport height monitoring
setupViewportHeightMonitoring();

// Initialize cross-device support
initCrossDeviceSupport();

// Unregister service worker in development mode
if (import.meta.env.DEV) {
   navigator.serviceWorker.getRegistrations().then((registrations) => {
     registrations.forEach((registration) => {
       registration.unregister();
       logger.log('[DEV] Service worker disabled for development');
     });
   });
  
  // Clear all caches
  caches.keys().then((names) => {
    names.forEach((name) => caches.delete(name));
  });
} else {
  // In production, check for service worker updates on visibility/focus
  // This ensures Android users get updates even if the app was suspended in background
  const checkForUpdates = () => {
    if ('serviceWorker' in navigator) {
      logger.log('🔄 [UPDATE CHECK] Checking for service worker updates...');
      navigator.serviceWorker.getRegistrations()
        .then((registrations) => {
          if (registrations.length === 0) {
            logger.log('⚠️ [UPDATE CHECK] No service worker registrations found');
            return;
          }
          logger.log(`✅ [UPDATE CHECK] Found ${registrations.length} service worker registration(s), checking for updates`);
          registrations.forEach((registration) => {
            registration.update()
              .then(() => {
                logger.log('✅ [UPDATE CHECK] Service worker update check completed');
              })
              .catch((error) => {
                logger.error('❌ [UPDATE CHECK] Service worker update check failed:', error);
              });
          });
        })
        .catch((error) => {
          logger.error('❌ [UPDATE CHECK] Failed to get service worker registrations:', error);
        });
    } else {
      logger.warn('⚠️ [UPDATE CHECK] Service workers not supported in this browser');
    }
  };

  // Check immediately on load
  checkForUpdates();

  // Check when document becomes visible (e.g., switching back to app on Android)
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      logger.log('👁️ [UPDATE CHECK] Document became visible, checking for updates');
      // Small delay to ensure app is fully active
      setTimeout(() => {
        checkForUpdates();
      }, 100);
    }
  });

  // Check when window receives focus
  window.addEventListener('focus', () => {
    logger.log('🎯 [UPDATE CHECK] Window focused, checking for updates');
    checkForUpdates();
  });
}

// Error Boundary for better error handling on iOS
class ErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('App Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          padding: '20px',
          backgroundColor: '#0b1220',
          color: 'white',
          fontFamily: 'system-ui, -apple-system, sans-serif'
        }}>
          <h1>Something went wrong</h1>
          <p>Please refresh the page to try again.</p>
          <button 
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 20px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              marginTop: '20px'
            }}
          >
            Refresh Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <MantineProvider theme={theme} defaultColorScheme="dark">
        <Notifications />
        <App />
      </MantineProvider>
    </ErrorBoundary>
  </StrictMode>
)